import { useState, useEffect, useCallback } from 'react';
import StatusDot from '@/components/common/StatusDot';
import { useNavigate } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { useAuthStore } from '@/store/auth.store';
import { useSupplyRequestStore } from '@/store/supply-request.store';
import { useSupplyRequestSocket } from '@/hooks/useSupplyRequestSocket';
import { useOfflineSupplyRequests } from '@/hooks/useOfflineSupplyRequests';
import { api } from '@/services/api';
import { toast } from '@/utils/toast';
import { offlineSupplyRequestService } from '../../offline/supply-request.service';
import type { SupplyRequest, PaginatedSupplyRequests } from '@/types/supply-request';
import { statusLabels, statusColors, priorityLabels, priorityColors } from '@/types/supply-request';
import FaIcon from '@/components/common/FaIcon';
import EmptyState from '@/components/common/EmptyState';
import { PlusIcon } from '@/components/icons';
import LoadingState from '@/components/common/LoadingState';
import Button, { IconButton } from '@/components/common/Button';
import MetricValue from '@/components/common/MetricValue';
import { SEVERITY_SPINE, type Severity } from '@/components/common/severity';

// Supply request priority → operational visual-weight scale
const SR_PRIORITY_RANK: Record<string, Severity> = {
  critical: 'critical',
  urgent: 'critical',
  high: 'warning',
  medium: 'elevated',
  low: 'calm',
};

type StatusFilter = 'all' | 'active' | 'completed';

export default function FieldSupplyRequests() {
  const navigate = useNavigate();
  const { activeIncident } = useIncidentStore();
  const { user } = useAuthStore();
  const { socketRefreshTrigger } = useSupplyRequestStore();
  useSupplyRequestSocket();
  const { unsyncedCount, isOffline, isSyncing, syncNow } = useOfflineSupplyRequests(activeIncident?.id);
  const [requests, setRequests] = useState<SupplyRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [pendingOffline, setPendingOffline] = useState(0);

  const checkOfflinePending = useCallback(async () => {
    try {
      const cached = await offlineSupplyRequestService.getCached(activeIncident?.id);
      const pending = cached.filter(r => !r.is_synced).length;
      setPendingOffline(pending + unsyncedCount);
    } catch {
      setPendingOffline(unsyncedCount);
    }
  }, [activeIncident?.id, unsyncedCount]);

  const loadRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ limit: '50', page: '1' });
      if (user?.id) params.append('requested_by', user.id);
      if (activeIncident?.id) params.append('incident_id', activeIncident.id);

      if (statusFilter === 'active') {
        params.append('status', 'draft,submitted,triage,pending_approval,approved,sourcing,ordered,in_transit');
      } else if (statusFilter === 'completed') {
        params.append('status', 'delivered,partially_fulfilled,completed,cancelled,rejected');
      }

      const data = await api.get<PaginatedSupplyRequests>(`/supply-requests?${params}`);
      setRequests(data.data || []);

      // Cache results for offline use
      if (data.data?.length) {
        offlineSupplyRequestService.cacheFromServer(data.data).catch(() => {});
      }
    } catch {
      // Fall back to offline cache
      try {
        const cached = await offlineSupplyRequestService.getCached(activeIncident?.id);
        const mapped = cached.map(c => c.data as SupplyRequest);
        setRequests(mapped);
        if (mapped.length > 0) {
          toast.info('Showing cached data (offline)');
        }
      } catch {
        toast.error('Failed to load supply requests');
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeIncident?.id, statusFilter, user?.id]);

  useEffect(() => {
    loadRequests();
    checkOfflinePending();
  }, [loadRequests, checkOfflinePending]);

  // Re-fetch when socket events update the store
  useEffect(() => {
    if (socketRefreshTrigger > 0) {
      loadRequests();
    }
  }, [socketRefreshTrigger, loadRequests]);

  if (isLoading) {
    return <LoadingState message="Loading requests..." />;
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="heading-3">My Supply Requests</h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-theme-tertiary">
            <MetricValue value={requests.length} className="font-semibold" /> total
          </span>
          <IconButton icon={<FaIcon icon="arrows-rotate" className="h-4 w-4" />} label="Refresh supply requests" onClick={() => loadRequests()} variant="ghost" size="sm" />
        </div>
      </div>

      {/* Offline pending indicator */}
      {pendingOffline > 0 && (
        <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <StatusDot color="yellow" pulse />
          <span className="flex-1 text-xs text-yellow-700 dark:text-yellow-300">
            <MetricValue value={pendingOffline} className="font-semibold" /> request{pendingOffline > 1 ? 's' : ''} pending sync
          </span>
          {!isOffline && (
            <Button
              onClick={() => syncNow()}
              variant="ghost"
              size="sm"
              loading={isSyncing}
              disabled={isSyncing}
            >
              {isSyncing ? 'Syncing...' : 'Sync'}
            </Button>
          )}
        </div>
      )}

      {/* Status filter tabs */}
      <div className="flex gap-1 bg-surface-inset rounded-lg p-1">
        {(['all', 'active', 'completed'] as StatusFilter[]).map((filter) => (
          <button
            key={filter}
            onClick={() => setStatusFilter(filter)}
            className={`flex-1 min-h-[44px] py-1.5 text-xs font-medium rounded-md transition-colors touch-manipulation capitalize ${
              statusFilter === filter
                ? 'bg-surface-primary text-theme-primary shadow-sm'
                : 'text-theme-secondary'
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {requests.length === 0 ? (
        <EmptyState
          variant="operational"
          icon="boxes-stacked"
          title="No supply requests"
          description="Create a supply request to get started."
          actionLabel="New Request"
          onAction={() => navigate('/field/supplies/new')}
        />
      ) : (
        <div className="space-y-2">
          {requests.map((r) => {
            const rank: Severity = SR_PRIORITY_RANK[r.priority] ?? 'calm';
            return (
            <div
              key={r.id}
              onClick={() => navigate(`/field/supplies/${r.id}`)}
              className={`w-full text-left bg-surface-primary rounded-lg border border-theme-primary p-3 min-h-[44px] touch-manipulation active:bg-surface-secondary cursor-pointer ${SEVERITY_SPINE[rank]}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-theme-primary truncate">{r.title}</p>
                  <p className="text-xs text-theme-tertiary mt-0.5">
                    <MetricValue value={`#${r.request_number}`} />
                    {r.items?.length > 0 && <> &middot; {r.items.length} item{r.items.length > 1 ? 's' : ''}</>}
                    {r.needed_by && <> &middot; Need by {new Date(r.needed_by).toLocaleDateString()}</>}
                  </p>
                  {r.delivery_location && (
                    <p className="text-xs text-theme-tertiary mt-0.5 truncate">{r.delivery_location}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[r.status] || statusColors.draft}`}>
                    {statusLabels[r.status] || r.status}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${priorityColors[r.priority] || priorityColors.medium}`}>
                    {priorityLabels[r.priority] || r.priority}
                  </span>
                </div>
              </div>
            </div>
          );})}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => navigate('/field/supplies/new')}
        aria-label="Create supply request"
        className="fixed right-4 z-30 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center touch-manipulation active:bg-primary-700"
        style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom) + 1rem)' }}
      >
        <PlusIcon className="h-7 w-7" />
      </button>
    </div>
  );
}
